import dayjs, { type Dayjs } from 'dayjs'
import { groupBy, partition } from 'lodash'
import { useCallback } from 'react'

import type { TaxBehavior } from '~/app/settings/(billing)/taxes/components/taxOptions'
import { allDaysOfWeek } from '~/components/forms/DaysOfWeekInput'
import type {
  BillableTimeOfUseRate,
  CreateProductRequest,
  EntitySelection,
  FlatRatePricing,
  PerUnitPricing,
  ProductEntitySelectionResponse,
  ProductResponse,
  TariffPassThroughPricing,
  TimeOfUsePricing,
  TimePeriod,
  VolumePricing,
  VolumeTierDto
} from '~/data/api/client/billing'
import { usePreferredOrgCurrency } from '~/data/api/hooks/organizations'
import type { ProductEntityType } from '~/data/types/billing'
import { BILLING_CURRENCIES, type BillingCurrency } from '~/data/types/currencies'
import { createCompositeConnectorId, parseStableConnectorId } from '~/data/utils/connector'
import {
  DEFAULT_CURRENCY_CODE,
  parseFromSmallestDenomination,
  parseToSmallestDenomination
} from '~/data/utils/currency'
import { useUserPrefs } from '~/hooks/useUserPrefs'

import { parseMetadataEntriesToObject, parseMetadataObjectToEntries } from '../agreements/utils'
import type { EntitySelectionFormData, ProductFormData } from './types'

/**
 * The tax behavior the server stored for a price, or unset when it returned none.
 *
 * Never derived from the currency or the merchant default: a derived value reads as a choice the merchant made, and the
 * next save would persist it over whatever the server actually applies. An update that omits the behavior keeps the
 * stored one.
 */
export function storedFormTaxBehavior(stored: TaxBehavior | null | undefined): TaxBehavior | undefined {
  return stored ?? undefined
}

/** Reference year used to bind yearly recurrence month-day values to the date picker; a leap year so Feb 29 stays selectable. */
const MONTH_DAY_REFERENCE_YEAR = 2024

/** Converts an ISO MonthDay wire value (--MM-dd) to a picker-compatible date string in the reference year. */
function monthDayToPickerDate(monthDay?: string | null): string | null {
  if (!monthDay) {
    return null
  }
  return `${MONTH_DAY_REFERENCE_YEAR}-${monthDay.replace(/^--/, '')}`
}

/** Extracts the month-day of a zone-less API timestamp as a picker date, for legacy recurring rates without a window. */
function timestampToPickerMonthDay(timestamp?: string | null): string | null {
  if (!timestamp) {
    return null
  }
  return `${MONTH_DAY_REFERENCE_YEAR}-${String(timestamp).slice(5, 10)}`
}

/** Converts a picker value (date string or Dayjs) to the ISO MonthDay wire format (--MM-dd). */
function pickerDateToMonthDay(value?: string | Dayjs | null): string | undefined {
  if (!value) {
    return undefined
  }
  // Hydrated values are bare YYYY-MM-DD; take MM-DD literally. Re-parsing date-only ISO
  // via dayjs/Date uses UTC midnight and can shift the calendar day west of UTC.
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return `--${value.slice(5, 10)}`
  }
  // Everything else (Dayjs objects, or the ISO strings coerceDayjsString produces during the
  // yup cast) goes through dayjs, the same way custom-tariff seasonal rates serialize to --MM-DD.
  const parsed = dayjs(value)
  return parsed.isValid() ? `--${parsed.format('MM-DD')}` : undefined
}

export const productEntityTypes: ProductEntityType[] = [
  'LOCATION',
  'EVSE',
  'CONNECTOR',
  'FLEET',
  'VEHICLE',
  'DRIVER',
  'ORGANIZATION'
]

/**
 * Safely validates and returns a currency, falling back to default currency if invalid
 */
export function getSafeCurrency(currency: unknown): BillingCurrency {
  return BILLING_CURRENCIES.includes(currency as BillingCurrency)
    ? (currency as BillingCurrency)
    : DEFAULT_CURRENCY_CODE
}

const DEFAULT_START_TIME = '00:00:00'
const DEFAULT_END_TIME = '23:59:59'
const END_OF_DAY_HOUR = 23
const END_OF_DAY_MINUTE = 59
const END_OF_DAY_SECOND = 59

function formatTimeForApi(
  time: Dayjs | null | undefined,
  fallback: string,
  options?: { preserveEndOfDay?: boolean }
): string {
  if (!time?.isValid()) return fallback

  const isEndOfDay =
    options?.preserveEndOfDay &&
    time.hour() === END_OF_DAY_HOUR &&
    time.minute() === END_OF_DAY_MINUTE &&
    time.second() === END_OF_DAY_SECOND

  const normalized = time.millisecond(0)
  const finalTime = isEndOfDay ? normalized : normalized.second(0)

  return finalTime.format('HH:mm:ss')
}

/**
 * The walk-up pre-authorization hold to send, in minor units. Omitted — which billing-api stores as "inherit the
 * platform default", on create and on update alike — unless the product is PUBLIC and the merchant switched a hold
 * on: billing-api rejects the field on any other visibility, and the form keeps stale values around when visibility
 * changes after the switch was set.
 */
function parseAuthorizationCharge(data: ProductFormData): number | undefined {
  if (data.visibility !== 'PUBLIC' || !data.authorizationChargeEnabled || !data.authorizationCharge) {
    return undefined
  }
  return Math.round(parseToSmallestDenomination(data.authorizationCharge))
}

/**
 * Converts ProductFormData to CreateProductRequest with proper typing based on pricing model.
 * Use with try / catch to handle errors!
 */
export function parseProductFormData(
  data: ProductFormData,
  {
    organizationId,
    omitMissingTaxBehavior = false
  }: { organizationId?: string; omitMissingTaxBehavior?: boolean } = {}
): CreateProductRequest {
  const selectedCurrency = getSafeCurrency(data.pricing.currency)
  // A new price must have one chosen (or seeded from the merchant's default) — falling back to INCLUSIVE here was a
  // silent revenue giveaway for US/CA prices the merchant never marked as tax-inclusive. It is left out only where
  // billing-api decides instead (see useTaxBehaviorOptional).
  const taxBehavior = data.pricing.taxBehavior
  if (!taxBehavior && !omitMissingTaxBehavior) {
    throw new Error('Tax behavior is required')
  }

  const baseRequest = {
    name: data.name,
    description: data.description,
    type: data.type,
    visibility: data.visibility,
    organizationId,
    // Classification is independent of inclusive/exclusive behavior: an inclusive (EU VAT) product still
    // needs its tax code so place-of-supply rules (EV charging vs general service) apply correctly.
    taxCode: data.taxCode || undefined,
    authorizationCharge: parseAuthorizationCharge(data),
    entitySelections: parseEntitySelections(data.entitySelections),
    // Always send metadata so updates can clear existing entries (API replaces when provided).
    metadata: parseMetadataEntriesToObject(data.metadataEntries) ?? {}
  }

  // Handle different pricing models using discriminated unions
  switch (data.pricing.pricingModel) {
    case 'FLAT': {
      if (!data.pricing.amount) {
        throw new Error('Amount is required for flat rate pricing')
      }

      const billingType = data.pricing.billingType ?? 'ONE_TIME'
      const recurringInterval = billingType === 'RECURRING' ? (data.pricing.billingPeriod ?? 'MONTH') : 'MONTH'

      const flatRatePricing: FlatRatePricing = {
        currency: selectedCurrency,
        unitAmount: Math.round(parseToSmallestDenomination(data.pricing.amount)), // Convert to cents
        billingType,
        recurringInterval,
        taxBehavior
      }

      return {
        ...baseRequest,
        flatRatePricing
      }
    }

    case 'USAGE_BASED': {
      if (!data.pricing.usagePricingType) {
        throw new Error('Usage pricing type is required for usage-based pricing')
      }

      switch (data.pricing.usagePricingType) {
        case 'PER_UNIT': {
          if (!data.pricing.amount) {
            throw new Error('Amount is required for per-unit pricing')
          }

          const perUnitPricing: PerUnitPricing = {
            currency: selectedCurrency,
            unitAmount: Math.round(parseToSmallestDenomination(data.pricing.amount)), // Convert to cents
            units: 'KWH', // Default unit for EV charging - could be made configurable
            billingType: 'ONE_TIME', // Hardcoded for now
            recurringInterval: 'MONTH', // Default
            taxBehavior
          }

          return {
            ...baseRequest,
            perUnitPricing
          }
        }

        case 'TIME_OF_USE': {
          if (!data.pricing.rates || data.pricing.rates.length === 0) {
            throw new Error('Rates are required for time-of-use pricing')
          }

          const timeOfUsePricing: TimeOfUsePricing = {
            currency: selectedCurrency,
            billingType: 'ONE_TIME', // Hardcoded for now
            recurringInterval: 'MONTH', // Default
            taxBehavior,
            rates: data.pricing.rates.map((rate) => {
              const idleFeeConfig = rate.hasIdleFee
                ? {
                    rate: Math.round(parseToSmallestDenomination(rate.idleFee?.rate || 0)),
                    gracePeriod: rate.idleFee?.gracePeriod ?? 0,
                    stepSize: rate.idleFee?.stepSize ?? 0
                  }
                : undefined

              const reservationConfig = rate.hasReservations
                ? {
                    price: 0, // Free reservations - only no-show penalty is configurable
                    noShowPenalty: Math.round(parseToSmallestDenomination(rate.reservationConfig?.noShowPenalty || 0)),
                    duration: 0,
                    minimumLeadTime: 0,
                    maximumLeadTime: 0,
                    cancellationWindow: 0
                  }
                : undefined

              return {
                name: rate.name,
                description: rate.description || '',
                // The effective range and the recurrence window are layered: validFrom/validTo bound
                // when the schedule exists (open-ended when validTo is blank) and, for yearly recurring
                // rates, the MonthDay window selects the part of each year the schedule applies.
                // validFrom is schema-required; converting it directly (no "now" fallback) makes a
                // blank value fail loudly here instead of silently creating a schedule that starts now.
                validFrom: dayjs(rate.validFrom).toISOString(),
                validTo: rate.validTo ? dayjs(rate.validTo).toISOString() : undefined,
                isYearlyRecurring: rate.isYearlyRecurring,
                recurrenceFrom: rate.isYearlyRecurring ? pickerDateToMonthDay(rate.recurrenceFrom) : undefined,
                recurrenceTo: rate.isYearlyRecurring ? pickerDateToMonthDay(rate.recurrenceTo) : undefined,
                isActive: rate.isActive,
                currency: selectedCurrency,
                initiationFee: rate.initiationFee
                  ? Math.round(parseToSmallestDenomination(rate.initiationFee))
                  : undefined, // Convert to cents
                idleFeeConfig,
                reservationConfig,
                lockRateAtPlugIn: rate.lockRateAtPlugIn ?? false,
                periods:
                  rate.periods?.map<TimePeriod>((period) => ({
                    name: period.name,
                    startTime: formatTimeForApi(period.startTime, DEFAULT_START_TIME),
                    endTime: formatTimeForApi(period.endTime, DEFAULT_END_TIME, { preserveEndOfDay: true }),
                    rate: Math.round(parseToSmallestDenomination(period.rate)), // Convert to cents
                    daysOfWeek: period.daysOfWeek
                  })) || [],
                restrictions:
                  rate.restrictions?.map((restriction) => ({
                    startTime: formatTimeForApi(restriction.startTime, DEFAULT_START_TIME),
                    endTime: formatTimeForApi(restriction.endTime, DEFAULT_END_TIME, { preserveEndOfDay: true }),
                    daysOfWeek: restriction.daysOfWeek,
                    maxDuration: restriction.maxDurationMinutes,
                    maxSoc: restriction.maxSocPercent
                  })) || []
              }
            })
          }

          return {
            ...baseRequest,
            timeOfUsePricing
          }
        }

        case 'TARIFF_PASS_THROUGH': {
          const tariffPassThroughPricing: TariffPassThroughPricing = {
            currency: selectedCurrency,
            billingType: 'ONE_TIME',
            recurringInterval: 'MONTH', // Default (unused for one-time usage pricing)
            // Premium is optional and defaults to a pure pass-through of the site's utility tariff.
            premium: Math.round(parseToSmallestDenomination(data.pricing.amount ?? 0)),
            taxBehavior
          }

          return {
            ...baseRequest,
            tariffPassThroughPricing
          }
        }

        default:
          throw new Error(`Unsupported usage pricing type: ${data.pricing.usagePricingType}`)
      }
    }

    case 'VOLUME_BASED': {
      if (
        !data.pricing.volumeTiers ||
        data.pricing.volumeTiers.length === 0 ||
        !data.pricing.tiersMode ||
        !data.pricing.tierQuantitySource
      ) {
        throw new Error('Volume tiers, tiers mode, and tier quantity source are required for volume pricing')
      }

      const billingType = data.pricing.billingType ?? 'ONE_TIME'
      const recurringInterval = billingType === 'RECURRING' ? (data.pricing.billingPeriod ?? 'MONTH') : 'MONTH'

      const volumeTiers: VolumeTierDto[] = data.pricing.volumeTiers.map((tier) => ({
        upTo: tier.upTo === null || tier.upTo === undefined || Number.isNaN(tier.upTo) ? undefined : tier.upTo,
        unitAmount: Math.round(parseToSmallestDenomination(tier.unitAmount)),
        flatAmount: tier.flatAmount != null ? Math.round(parseToSmallestDenomination(tier.flatAmount)) : undefined
      }))

      const volumePricing: VolumePricing = {
        currency: selectedCurrency,
        billingType,
        recurringInterval,
        units: data.pricing.tierQuantitySource === 'ENTITY_COUNT' ? 'PC' : 'KWH',
        tiersMode: data.pricing.tiersMode,
        tierQuantitySource: data.pricing.tierQuantitySource,
        volumeTiers,
        taxBehavior
      }

      return {
        ...baseRequest,
        volumePricing
      }
    }

    default:
      throw new Error(`Unsupported pricing model: ${data.pricing.pricingModel}`)
  }
}

/**
 * Converts ProductResponse to ProductFormData for form editing
 */
export function useParseProductResponse() {
  const { tzDayjs } = useUserPrefs()
  const defaultCurrency = usePreferredOrgCurrency()

  return useCallback(
    (product: ProductResponse): ProductFormData => {
      if (!product.name || !product.type || !product.visibility) {
        throw new Error('Product is missing required fields: name, type, or visibility')
      }

      const baseFormData = {
        name: product.name,
        description: product.description || '',
        type: product.type,
        visibility: product.visibility,
        taxCode: product.taxCode || undefined,
        authorizationChargeEnabled: product.authorizationCharge != null,
        authorizationCharge:
          product.authorizationCharge != null ? parseFromSmallestDenomination(product.authorizationCharge) : undefined,
        metadataEntries: parseMetadataObjectToEntries(product.metadata),
        entitySelections: toFormEntitySelections(product.entitySelections || [])
      }

      // Flat rate pricing
      if (product.flatRatePricing) {
        return {
          ...baseFormData,
          pricing: {
            pricingModel: 'FLAT',
            amount: parseFromSmallestDenomination(product.flatRatePricing.unitAmount || 0),
            currency: getSafeCurrency(product.flatRatePricing.currency),
            billingType: product.flatRatePricing.billingType ?? 'ONE_TIME',
            billingPeriod: product.flatRatePricing.recurringInterval || 'MONTH',
            taxBehavior: storedFormTaxBehavior(product.flatRatePricing.taxBehavior)
          }
        }
      }

      // Utility tariff pass-through pricing
      if (product.tariffPassThroughPricing) {
        return {
          ...baseFormData,
          pricing: {
            pricingModel: 'USAGE_BASED',
            usagePricingType: 'TARIFF_PASS_THROUGH',
            amount: parseFromSmallestDenomination(product.tariffPassThroughPricing.premium || 0),
            currency: getSafeCurrency(product.tariffPassThroughPricing.currency),
            taxBehavior: storedFormTaxBehavior(product.tariffPassThroughPricing.taxBehavior)
          }
        }
      }

      // Per unit pricing
      if (product.perUnitPricing) {
        return {
          ...baseFormData,
          pricing: {
            pricingModel: 'USAGE_BASED',
            usagePricingType: 'PER_UNIT',
            amount: parseFromSmallestDenomination(product.perUnitPricing.unitAmount || 0),
            currency: getSafeCurrency(product.perUnitPricing.currency),
            billingPeriod: product.perUnitPricing.recurringInterval || 'MONTH',
            taxBehavior: storedFormTaxBehavior(product.perUnitPricing.taxBehavior)
          }
        }
      }

      // Volume pricing
      if (product.volumePricing) {
        return {
          ...baseFormData,
          pricing: {
            pricingModel: 'VOLUME_BASED',
            currency: getSafeCurrency(product.volumePricing.currency),
            billingType: product.volumePricing.billingType ?? 'ONE_TIME',
            billingPeriod: product.volumePricing.recurringInterval || 'MONTH',
            tiersMode: product.volumePricing.tiersMode,
            tierQuantitySource: product.volumePricing.tierQuantitySource,
            taxBehavior: storedFormTaxBehavior(product.volumePricing.taxBehavior),
            volumeTiers:
              product.volumePricing.volumeTiers?.map((t) => ({
                upTo: t.upTo ?? null,
                unitAmount: parseFromSmallestDenomination(t.unitAmount ?? 0),
                flatAmount: t.flatAmount != null ? parseFromSmallestDenomination(t.flatAmount) : null
              })) ?? []
          }
        }
      }

      // Time of use pricing
      if (product.timeOfUsePricing) {
        return {
          ...baseFormData,
          pricing: {
            pricingModel: 'USAGE_BASED',
            usagePricingType: 'TIME_OF_USE',
            currency: getSafeCurrency(product.timeOfUsePricing.currency),
            billingPeriod: product.timeOfUsePricing.recurringInterval || 'MONTH',
            taxBehavior: storedFormTaxBehavior(product.timeOfUsePricing.taxBehavior),
            rates: parseTimeOfUseRatesToForm(product.timeOfUsePricing.rates, tzDayjs)
          }
        }
      }

      // Default fallback
      return {
        ...baseFormData,
        pricing: {
          pricingModel: 'USAGE_BASED' as const,
          usagePricingType: 'TIME_OF_USE' as const,
          currency: defaultCurrency,
          // Deliberately unset on a new product: the tax behavior cards (and the wizard) pre-select the
          // merchant's own default once their tax settings load. Seeding INCLUSIVE here would read as an
          // answer the merchant gave. Submit refuses to invent one if nothing is ever chosen.
          rates: [
            {
              name: '',
              validFrom: tzDayjs().toISOString(),
              isYearlyRecurring: false,
              isActive: true,
              periods: [
                {
                  name: '',
                  daysOfWeek: allDaysOfWeek,
                  startTime: tzDayjs().startOf('day'),
                  endTime: tzDayjs().endOf('day'),
                  rate: 0
                }
              ],
              hasIdleFee: false,
              hasReservations: false,
              lockRateAtPlugIn: false
            }
          ]
        }
      }
    },
    [tzDayjs, defaultCurrency]
  )
}

/**
 * Hydrates API time-of-use rates into the wizard's rate shape (cents → major units, `HH:mm:ss` → Dayjs
 * pickers in the user's zone, idle-fee / reservation toggles). Shared by the edit form and the billing
 * composer's draft mapper so both produce identical form state.
 */
export function parseTimeOfUseRatesToForm(
  rates: BillableTimeOfUseRate[] | undefined,
  tzDayjs: () => Dayjs
): NonNullable<ProductFormData['pricing']['rates']> {
  return (
    rates?.map((rate) => ({
      ...rate,
      // Legacy recurring rates predate the recurrence window: prefill the window pickers from
      // the year-stamped validity dates and clear validTo, so re-saving upgrades them to an
      // open-ended recurring schedule (the same transform the API-side backfill applies).
      validTo: rate.isYearlyRecurring && !rate.recurrenceFrom ? null : rate.validTo || null,
      isYearlyRecurring: rate.isYearlyRecurring ?? false,
      recurrenceFrom:
        monthDayToPickerDate(rate.recurrenceFrom) ??
        (rate.isYearlyRecurring ? timestampToPickerMonthDay(rate.validFrom) : null),
      recurrenceTo:
        monthDayToPickerDate(rate.recurrenceTo) ?? (rate.isYearlyRecurring ? timestampToPickerMonthDay(rate.validTo) : null),
      isActive: rate.isActive ?? true,
      initiationFee: rate.initiationFee ? parseFromSmallestDenomination(rate.initiationFee) : undefined,
      lockRateAtPlugIn: rate.lockRateAtPlugIn ?? false,
      hasIdleFee: Boolean(rate.idleFeeConfig),
      idleFee: rate.idleFeeConfig
        ? {
            rate: parseFromSmallestDenomination(rate.idleFeeConfig.rate || 0),
            gracePeriod: rate.idleFeeConfig.gracePeriod ?? null,
            stepSize: rate.idleFeeConfig.stepSize ?? null
          }
        : undefined,
      hasReservations: Boolean(rate.reservationConfig),
      reservationConfig: rate.reservationConfig
        ? {
            noShowPenalty: parseFromSmallestDenomination(rate.reservationConfig.noShowPenalty || 0)
          }
        : undefined,
      periods:
        rate.periods?.map((period) => ({
          ...period,
          name: period.name || '',
          daysOfWeek: period.daysOfWeek || allDaysOfWeek,
          startTime: tzDayjs()
            .set('hour', splitTime(period.startTime, DEFAULT_START_TIME).hours)
            .set('minute', splitTime(period.startTime, DEFAULT_START_TIME).minutes),
          endTime: tzDayjs()
            .set('hour', splitTime(period.endTime, DEFAULT_END_TIME).hours)
            .set('minute', splitTime(period.endTime, DEFAULT_END_TIME).minutes),
          rate: parseFromSmallestDenomination(period.rate || 0)
        })) || [],
      restrictions:
        rate.restrictions?.map((restriction) => ({
          startTime: tzDayjs()
            .set('hour', splitTime(restriction.startTime ?? DEFAULT_START_TIME).hours)
            .set('minute', splitTime(restriction.startTime ?? DEFAULT_START_TIME).minutes),
          endTime: tzDayjs()
            .set('hour', splitTime(restriction.endTime ?? DEFAULT_END_TIME).hours)
            .set('minute', splitTime(restriction.endTime ?? DEFAULT_END_TIME).minutes),
          daysOfWeek: restriction.daysOfWeek ?? [],
          maxDurationMinutes: restriction.maxDuration ?? undefined,
          maxSocPercent: restriction.maxSoc ?? undefined
        })) || []
    })) ?? []
  )
}

function splitTime(time: string, defaultTime = '00:00:00') {
  const [hours, minutes] = time.split(':') || defaultTime.split(':')
  return {
    hours: parseInt(hours!),
    minutes: parseInt(minutes!)
  }
}

export function toFormEntitySelections(apiSelections: ProductEntitySelectionResponse[]): EntitySelectionFormData[] {
  if (!apiSelections || apiSelections.length === 0) return []

  const formSelections: EntitySelectionFormData[] = []

  for (const sel of apiSelections) {
    if (!sel || !sel.entityId || !sel.entityType) continue // Skip invalid selections

    // Handle EVSE/CHARGER entities - normalize CHARGER to EVSE for consistency
    const isEvse = sel.entityType === 'EVSE' || sel.entityType === 'CHARGER'

    // Check if EVSE has connector children to expand
    const hasConnectorChildren =
      isEvse &&
      sel.children &&
      sel.children.length > 0 &&
      sel.children.some((child) => child.entityType === 'CONNECTOR')

    if (hasConnectorChildren) {
      // Expand EVSE children CONNECTORs into composite connector items
      const evseId = sel.entityId as string
      for (const child of sel.children!) {
        if (child.entityType === 'CONNECTOR' && child.entityId != null) {
          // child.entityId may be a bare connector id (e.g. "3") or an already-composite
          // "chargerId--connectorId". Preserve composites; build one for bare numeric ids.
          // Otherwise composite children get dropped from the wizard while the detail page still lists them.
          if (parseStableConnectorId(child.entityId)) {
            formSelections.push({ entityType: 'CONNECTOR', entityId: child.entityId })
          } else {
            const connectorIdNum = Number(child.entityId)
            if (!Number.isNaN(connectorIdNum)) {
              formSelections.push({
                entityType: 'CONNECTOR',
                entityId: createCompositeConnectorId({ chargerId: evseId, connectorId: connectorIdNum })
              })
            }
          }
        }
      }
      // When EVSE has connector children, we expand to connectors and don't include the EVSE itself
      // The tri-state selection will show the charger as selected when all connectors are selected
      continue
    }

    // Pass through all other entities (including EVSE/CHARGER without children or without connector children, LOCATION, etc.)
    // Normalize CHARGER -> EVSE and SITE -> LOCATION so the form/tri-state uses one canonical
    // type per level. The asset rows and hydration only understand EVSE and LOCATION, so an
    // un-normalized SITE selection would render on the detail page but stay unchecked in the wizard.
    const normalizedType =
      sel.entityType === 'CHARGER' ? 'EVSE' : sel.entityType === 'SITE' ? 'LOCATION' : sel.entityType
    formSelections.push({
      entityType: normalizedType as ProductEntityType,
      entityId: sel.entityId
    })
  }

  return formSelections
}

const parseEntitySelections = (entitySelections: EntitySelectionFormData[]): EntitySelection[] => {
  if (!entitySelections || entitySelections.length === 0) return []

  const [connectorSelections, otherSelections] = partition(entitySelections, (s) => s.entityType === 'CONNECTOR')

  const validParsed = connectorSelections
    .map((s) => ({ parsed: parseStableConnectorId(s.entityId) }))
    .filter((p): p is { parsed: { chargerId: string; connectorId: string } } => Boolean(p.parsed))

  const groupedByEvse = groupBy(validParsed, (p) => p.parsed.chargerId)
  const evseSelections: EntitySelection[] = Object.entries(groupedByEvse).map(([evseId, group]) => ({
    entityType: 'EVSE',
    entityId: evseId,
    children: group.map((g) => ({ entityType: 'CONNECTOR', entityId: g.parsed.connectorId }))
  }))

  const passthroughOthers: EntitySelection[] = otherSelections.map((s) => ({
    entityId: s.entityId,
    entityType: s.entityType
  }))

  return [...evseSelections, ...passthroughOthers]
}
